import type { Report } from '@ofmos/shared/report';
import type { ReactNode } from 'react';
import { useNavigate } from 'react-router';
import type { ObjectMetadata } from '../../data/client';
import { Button, type ChartPoint, type ChartSeries, EmptyState, Icon, Skeleton } from '../../ui';
import { MOTION, useMotion } from '../../ui/motion';
import { PaneHost } from '../shell/PaneHost';
import { ReportChart } from './ReportChart';
import { ReportTableView } from './ReportTableView';
import type { ReportChartData } from './reportSeries';
import { formatCount, type ReportView, rangeLabel } from './reportsModel';
import { isOverTime, takeawayLine } from './takeaway';

/**
 * THE DETAIL PANE (`spec/screens/reports.md` section 2 item 4).
 *
 * Reading order, and it is the order the spec sets: the takeaway sentence at the top, the
 * chart below it filling the pane's width, the legend under the chart, and the table in
 * the chart's place when the toolbar says Table. A chart under a sentence is evidence for
 * a claim; a chart over one is a puzzle with the answer printed underneath.
 *
 * THE TAKEAWAY IS AT THE BODY SIZE IN THE PRIMARY LABEL COLOUR, which is the second half
 * of the spec's own HYPOTHESIS ("the takeaway sentence at 22px reads as the report's
 * heading; if it competes with the window title, it drops to the body size in the primary
 * label colour"). It competes: `.shell-window-title` computes 15px at and above the
 * desktop breakpoint (index.css) and `--text-lg` is 22px, so the 22px line would be the
 * LARGEST heading on a screen whose window title is the report's name - and section 7's
 * own rule from the Apple images is "Apple does not put more than one bold heading over a
 * chart area: one title line at the top of the detail, then the content". Read in the
 * running browser rather than off the stylesheet; the numbers are in the slice record.
 *
 * A CLICK ON A MARK OPENS THE RECORDS BEHIND IT. The sheet this used to open (ledger D-11)
 * is gone: the spec says a click reaches the object's list. What it cannot yet carry is
 * the FILTER - `app/records/views/urlContract.ts` has `view` and `mode` and no ad-hoc
 * filter parameter, and `RecordListPage` seeds its filter from component state rather than
 * from the URL - so the trip lands on the object's list unfiltered and the ledger names
 * exactly what is missing.
 */
export interface ReportDetailProps {
  readonly report: Report;
  readonly object: ObjectMetadata | undefined;
  readonly data: ReportChartData;
  readonly view: ReportView;
  readonly range: Report['definition']['range'];
  readonly loading: boolean;
  readonly refetching: boolean;
  readonly error: Error | null;
  readonly offline: boolean;
  readonly onRetry: () => void;
  /**
   * THE PHONE'S RANGE CONTROL, WHICH SITS UNDER THE CHART (section 3 item 2). A navigation
   * bar has no centre area, so the control the desktop toolbar carries becomes the first
   * row under the drawing - which is where iOS puts a scope control on a pushed screen.
   */
  readonly rangeControl?: ReactNode | undefined;
  /**
   * THE PHONE'S CHART | TABLE CONTROL, WHICH SITS OVER THE CHART (section 3 item 2 as
   * corrected on 2026-09-16, wave 5, triage E-W5-1).
   *
   * It used to ride in the navigation bar's trailing slot beside three symbol items, and
   * the bar had no room left for the one thing the HIG says a bar owes a reader: "Provide
   * a useful title for each window. A title helps people confirm their location" and "keep
   * the title under 15 characters long so you leave enough room for other controls"
   * (hig: https://developer.apple.com/design/human-interface-guidelines/toolbars). The
   * report's name computed to a rect of width 0. So the control comes down into the
   * content, directly under the takeaway line and over the drawing it switches - which is
   * where the HIG's own example puts one: "In the iOS Health app, a segmented control
   * provides a choice of time ranges for the activity graphs to display"
   * (hig: https://developer.apple.com/design/human-interface-guidelines/segmented-controls).
   */
  readonly viewControl?: ReactNode | undefined;
}

/** The two views share a slot while the outgoing view finishes its tokenized fade. */
function ReportPresentation({
  view,
  present,
  children,
}: {
  readonly view: ReportView;
  readonly present: boolean;
  readonly children: ReactNode;
}) {
  const motion = useMotion(present);
  if (!motion.mounted) return null;
  return (
    <div
      ref={motion.ref}
      data-report-view={view}
      data-state={motion.state}
      className={`${MOTION.fade} col-start-1 row-start-1 min-w-0`}
      inert={!present}
      aria-hidden={!present}
    >
      {children}
    </div>
  );
}

export function ReportDetail({
  report,
  object,
  data,
  view,
  range,
  loading,
  refetching,
  error,
  offline,
  onRetry,
  rangeControl,
  viewControl,
}: ReportDetailProps) {
  const navigate = useNavigate();
  const definition = report.definition;
  const objectId = definition.objectId;

  /**
   * A SERIES WITH NO DATA SAYS SO IN THE LEGEND (section 4, Partial). The legend is built
   * from the series' own labels, so the sentence belongs to the label: a series drawn as
   * nothing and listed as though it were there is the one state a reader cannot tell from
   * a bug.
   */
  const series: readonly ChartSeries[] = data.series.map((entry) =>
    entry.points.every((point) => point.value === null)
      ? { ...entry, label: `${entry.label} (no data)` }
      : entry,
  );

  const takeaway = takeawayLine({
    chart: definition.chart,
    points: data.points,
    format: formatCount,
    rangeLabel: rangeLabel(range),
    overTime: isOverTime(definition),
  });

  const drill = (point: ChartPoint) => {
    void navigate(`/objects/${objectId}`, { state: { fromReport: report.id, group: point.key } });
  };

  return (
    <section className="reports-detail" aria-label={report.name}>
      {/*
        THE OPEN REPORT IS THE PANE'S SCROLLPORT AND IT BEGINS AT THE BAR'S TOP EDGE (wave 5
        round 5, G5; owner decision C6-5, "the glass toolbar everywhere it belongs").

        THE CAUSE IT FIXES. `.reports-detail-body` was a scroller that started at the bar's
        LOWER edge, so the chart never passed under the bar and the bar's hard edge - a 6px
        blur under a flat tint - stood on a constant field, which returns the constant
        (`doc:` docs/decisions/ui-foundation-w5.md, A0). Measured on main 94b2f9d at 1440
        light: the bar's own box read a standard deviation of 0.000 with the bar out of the
        shot. The host is the same one the object list and Tasks already mount.

        THE BODY STAYS INSIDE THE HOST RATHER THAN WEARING IT, and that is not a preference:
        `.reports-detail-body` declares `padding-block`, `.shell-pane-host` declares
        `padding-block-start`, both live in the `utilities` layer at one class of specificity,
        and the later rule wins - so the class on the host cut the bar's own 52 down to 16 and
        the report's takeaway sentence rested inside the band at scroll 0, which is wave 4
        round 2's zero overhang (C2-21). Measured on the running build before the fix:
        `padding-block-start` 16px with the first line at y 16.
      */}
      <PaneHost host="report-detail">
        <div className="reports-detail-body">
          {offline && (
            /*
            THE OFFLINE NOTICE (components/MAP.md "Offline notice"): one opaque line under
            the toolbar with a warning symbol and one sentence, persistent while offline, no
            auto-dismiss and no button, and the last rendered chart stays under it. Derived
            from the kit's notification row and the HIG's feedback rule, because Apple ships
            no in-window offline banner.

            IT STANDS INSIDE THE SCROLLPORT (wave 5 round 5, G5). It used to be a sibling
            ABOVE the pane's body, which is where the host now begins: a line between the bar
            and the host pushes the host down by its own height and takes the same amount of
            the bar's band away from it, which is the defect ledger G5-W5-2 records for
            `PageHeader`'s subtitle. "Persistent" here means it stays while the connection is
            gone, not that it is pinned; at rest it is still the first line under the bar.
          */
            <p className="reports-offline" role="status">
              <Icon name="warning" size="1rem" />
              Offline. The numbers on screen are the last ones loaded.
            </p>
          )}
          {error !== null ? (
            /*
            ERROR, INLINE, WITH THE FIX (section 4, copy deck R8: next to the problem, what
            to do next, never "Error" and never "we").
          */
            <div className="reports-error">
              <p className="text-sm text-ink">This report could not be read. Try again.</p>
              <Button variant="secondary" onClick={onRetry}>
                Retry
              </Button>
            </div>
          ) : loading ? (
            <div className="reports-loading" aria-busy="true">
              {/*
              THE AXIS SKELETON (section 4, Loading). The value gutter and the plot keep
              their geometry so the chart does not jump into place when the numbers land -
              which is what a skeleton is for and what a spinner cannot do.
            */}
              <Skeleton className="h-3 w-64" label="Loading this report." />
              <div className="reports-loading-plot">
                <div className="reports-loading-gutter">
                  <Skeleton className="h-2 w-10" />
                  <Skeleton className="h-2 w-10" />
                  <Skeleton className="h-2 w-10" />
                  <Skeleton className="h-2 w-10" />
                </div>
                <Skeleton shape="block" className="h-full" />
              </div>
            </div>
          ) : (
            <>
              {takeaway !== null && (
                <p data-reports-takeaway="line" className="reports-takeaway">
                  {takeaway}
                </p>
              )}

              {viewControl !== undefined && (
                /*
                FULL WIDTH, AND THAT IS THE CONTENT'S RULE RATHER THAN THE BAR'S: in a
                toolbar a segmented control is one item among several and takes the width
                of its labels; over the content it is the scope of everything under it, so
                it spans the same column the drawing does.
              */
                <div className="reports-phone-view">{viewControl}</div>
              )}

              <div className="grid">
                <ReportPresentation view="table" present={view === 'table'}>
                  <ReportTableView
                    series={series}
                    points={data.points}
                    categoryHeading={object?.labelSingular ?? 'Group'}
                  />
                </ReportPresentation>
                <ReportPresentation view="chart" present={view === 'chart'}>
                  {data.points.length === 0 && data.total === null ? (
                    /* Copy deck R4: a noun title and the move that widens the answer. */
                    <EmptyState
                      icon="search"
                      title="No data"
                      description={`No ${(object?.labelPlural ?? 'records').toLowerCase()} in this range. Widen the range.`}
                    />
                  ) : (
                    <ReportChart
                      definition={definition}
                      name={report.name}
                      titleHidden
                      height={320}
                      refetching={refetching}
                      series={series}
                      points={data.points}
                      total={data.total}
                      format={formatCount}
                      {...(definition.chart === 'number' ? {} : { onSelect: drill })}
                    />
                  )}
                </ReportPresentation>
              </div>
              {rangeControl !== undefined && (
                <div className="reports-phone-range">{rangeControl}</div>
              )}
            </>
          )}
        </div>
      </PaneHost>
    </section>
  );
}
